import { useEffect, useState, useCallback } from 'react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { AlertTriangle, CheckCircle2, XCircle, ExternalLink, Filter, RefreshCw } from 'lucide-react';
import { useCan, Can } from '../../lib/rbac';

type Status = 'pending' | 'claim_filed' | 'claim_won' | 'claim_lost' | 'not_eligible' | 'manual_review';
type DetectionType = 'not_delivered_no_amazon_reversal' | 'mediation_without_claim' | 'mp_reserve_dispute_old' | 'delayed_shipment_loss';

interface PendingClaim {
  id: number;
  seller_id: number;
  ml_order_id: number;
  context_site: string | null;
  detection_type: DetectionType;
  detected_at: string;
  amazon_cost_lost: string;
  ml_shipping_lost: string;
  ml_fee_lost: string;
  mp_held_amount: string;
  mediation_charged: string;
  total_potential_loss: string;
  status: Status;
  ml_claim_id: number | null;
  ml_claim_status: string | null;
  ml_resolution_benefited: string | null;
  filed_at: string | null;
  resolved_at: string | null;
  recovered_amount: string | null;
  notes: string | null;
}

interface Summary {
  by_status: Array<{
    status: Status;
    detection_type: DetectionType;
    context_site: string | null;
    count: number;
    total_loss: number;
    recovered: number;
  }>;
  totals: {
    pending_count: number;
    pending_loss: number;
    recovered: number;
    written_off: number;
  };
}

const STATUS_LABEL: Record<Status, string> = {
  pending: 'Pendiente',
  claim_filed: 'Reclamado',
  claim_won: 'Cubierto ML',
  claim_lost: 'Perdido',
  not_eligible: 'No aplica',
  manual_review: 'Revisión manual',
};

const STATUS_COLOR: Record<Status, string> = {
  pending: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30',
  claim_filed: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30',
  claim_won: 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30',
  claim_lost: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30',
  not_eligible: 'bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/30',
  manual_review: 'bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/30',
};

const DETECTION_LABEL: Record<DetectionType, string> = {
  not_delivered_no_amazon_reversal: 'No entregado',
  mediation_without_claim: 'Mediación s/ claim',
  mp_reserve_dispute_old: 'Reserva MP vieja',
  delayed_shipment_loss: 'Envío atrasado',
};

const fmt = (n: number | string) => `$${Number(n).toFixed(2)}`;

export function PendingClaimsSection() {
  const can = useCan();
  const canOperate = can('finance.operate');
  const sellerId = useUIStore(s => s.sellerId);
  const [rows, setRows] = useState<PendingClaim[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<Status[]>(['pending', 'claim_filed', 'manual_review']);
  const [detectionFilter, setDetectionFilter] = useState<DetectionType | ''>('');
  const [siteFilter, setSiteFilter] = useState('');
  const [drillId, setDrillId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ seller_id: String(sellerId), limit: '200' });
      if (statusFilter.length) params.set('status', statusFilter.join(','));
      if (detectionFilter) params.set('detection_type', detectionFilter);
      if (siteFilter) params.set('site', siteFilter);

      const [listRes, summaryRes] = await Promise.all([
        api.get<{ rows: PendingClaim[]; total: number }>(`/finance/pending-claims?${params}`),
        api.get<Summary>(`/finance/pending-claims/summary?seller_id=${sellerId}`),
      ]);
      if (listRes.data) { setRows(listRes.data.rows); setTotal(listRes.data.total); }
      if (summaryRes.data) setSummary(summaryRes.data);
    } catch (err) {
      console.error('[pending-claims] load:', err);
    } finally {
      setLoading(false);
    }
  }, [sellerId, statusFilter, detectionFilter, siteFilter]);

  useEffect(() => { load(); }, [load]);

  const runDetection = async () => {
    setLoading(true);
    try {
      const result = await api.post<{ inserted_not_delivered: number; inserted_mediation: number }>(
        `/finance/pending-claims/detect`, {},
      );
      const d = result.data;
      const total = (d?.inserted_not_delivered ?? 0) + (d?.inserted_mediation ?? 0);
      alert(`Detección: ${total} candidatos nuevos (${d?.inserted_not_delivered ?? 0} not_delivered + ${d?.inserted_mediation ?? 0} mediation)`);
      await load();
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    }
  };

  const runSync = async () => {
    setLoading(true);
    try {
      const result = await api.post<{
        pending_cases_processed: number; claims_fetched: number;
        newly_linked: number; ledger_emitted: number; errors: any[];
      }>(`/finance/pending-claims/sync`, {});
      const d = result.data;
      alert(
        `Sync ML completado:\n` +
        `  Casos procesados: ${d?.pending_cases_processed ?? 0}\n` +
        `  Claims encontrados en ML: ${d?.claims_fetched ?? 0}\n` +
        `  Vinculados: ${d?.newly_linked ?? 0}\n` +
        `  Ledger events emitidos: ${d?.ledger_emitted ?? 0}\n` +
        `  Errores: ${d?.errors?.length ?? 0}`,
      );
      await load();
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    }
  };

  const updateStatus = async (id: number, newStatus: Status, recovered?: number) => {
    try {
      await api.put(`/finance/pending-claims/${id}`, {
        status: newStatus,
        ...(recovered !== undefined ? { recovered_amount: recovered } : {}),
      });
      await load();
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    }
  };

  const toggleStatusFilter = (s: Status) => {
    setStatusFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  return (
    <div className="space-y-4">
      {/* Summary KPI cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KPICard
            label="Pendientes"
            value={`${summary.totals.pending_count}`}
            sub={fmt(summary.totals.pending_loss)}
            icon={<AlertTriangle className="w-4 h-4 text-amber-500" />}
          />
          <KPICard
            label="Recuperado"
            value={fmt(summary.totals.recovered)}
            sub="claim_won"
            icon={<CheckCircle2 className="w-4 h-4 text-green-500" />}
          />
          <KPICard
            label="Pérdida confirmada"
            value={fmt(summary.totals.written_off)}
            sub="lost / not_eligible"
            icon={<XCircle className="w-4 h-4 text-red-500" />}
          />
          <KPICard
            label="Tasa de recuperación"
            value={
              summary.totals.recovered + summary.totals.written_off > 0
                ? `${((summary.totals.recovered / (summary.totals.recovered + summary.totals.written_off)) * 100).toFixed(1)}%`
                : '—'
            }
            sub="resueltos"
            icon={<TrendingIcon />}
          />
        </div>
      )}

      {/* Filters + actions */}
      <div className="flex flex-wrap items-center gap-2 bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-3">
        <Filter className="w-4 h-4 text-[var(--color-text-secondary)]" />
        <span className="text-xs text-[var(--color-text-secondary)]">Status:</span>
        {(Object.keys(STATUS_LABEL) as Status[]).map(s => (
          <button
            key={s}
            onClick={() => toggleStatusFilter(s)}
            className={`px-2 py-1 text-xs border rounded transition-colors ${
              statusFilter.includes(s) ? STATUS_COLOR[s] : 'border-[var(--color-border)] text-[var(--color-text-secondary)]'
            }`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
        <select
          value={detectionFilter}
          onChange={e => setDetectionFilter(e.target.value as DetectionType | '')}
          className="ml-2 px-2 py-1 text-xs bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded"
        >
          <option value="">Todos los tipos</option>
          {(Object.keys(DETECTION_LABEL) as DetectionType[]).map(d => (
            <option key={d} value={d}>{DETECTION_LABEL[d]}</option>
          ))}
        </select>
        <select
          value={siteFilter}
          onChange={e => setSiteFilter(e.target.value)}
          className="px-2 py-1 text-xs bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded"
        >
          <option value="">Todos los sites</option>
          {['MLA', 'MLB', 'MLC', 'MLM', 'MCO', 'CBT'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <Can cap="finance.operate">
          <button
            onClick={runSync}
            className="ml-auto flex items-center gap-1 px-3 py-1 text-xs bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 rounded transition-colors"
            title="Buscar claims existentes en ML y vincularlos"
          >
            <RefreshCw className="w-3 h-3" />
            Sync con ML
          </button>
        </Can>
        <Can cap="finance.operate">
          <button
            onClick={runDetection}
            className="flex items-center gap-1 px-3 py-1 text-xs bg-[var(--color-primary)]/10 hover:bg-[var(--color-primary)]/20 text-[var(--color-primary)] rounded transition-colors"
            title="Re-correr detección de candidatos nuevos en CBT"
          >
            <RefreshCw className="w-3 h-3" />
            Re-detectar
          </button>
        </Can>
      </div>

      {/* Table */}
      <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-[var(--color-border)] text-xs text-[var(--color-text-secondary)]">
          {loading ? 'Cargando...' : `${total} casos`}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg-subtle)] text-xs text-[var(--color-text-secondary)] uppercase">
              <tr>
                <th className="text-left px-3 py-2">Order</th>
                <th className="text-left px-3 py-2">Site</th>
                <th className="text-left px-3 py-2">Tipo</th>
                <th className="text-right px-3 py-2">Pérdida potencial</th>
                <th className="text-right px-3 py-2">Amazon</th>
                <th className="text-right px-3 py-2">Mediation</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Detectado</th>
                <th className="text-left px-3 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg-subtle)]">
                  <td className="px-3 py-2 font-mono text-xs">
                    <button onClick={() => setDrillId(r.id)} className="text-[var(--color-primary)] hover:underline">
                      {r.ml_order_id}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs">{r.context_site ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">{DETECTION_LABEL[r.detection_type]}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(r.total_potential_loss)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-[var(--color-text-secondary)]">{fmt(r.amazon_cost_lost)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-[var(--color-text-secondary)]">{fmt(r.mediation_charged)}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 text-xs border rounded ${STATUS_COLOR[r.status]}`}>
                      {STATUS_LABEL[r.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-[var(--color-text-secondary)]">{r.detected_at?.slice(0, 10)}</td>
                  <td className="px-3 py-2 text-xs">
                    {/* Sin permiso de operar: guion, nunca celda vacía. */}
                    {!canOperate && <span className="text-[var(--color-text-secondary)]">—</span>}
                    <div className="flex gap-1">
                      {canOperate && r.status === 'pending' && (
                        <button onClick={() => updateStatus(r.id, 'claim_filed')}
                          className="px-2 py-0.5 bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 rounded text-xs">
                          Reclamado
                        </button>
                      )}
                      {canOperate && (r.status === 'pending' || r.status === 'claim_filed') && (
                        <>
                          <button onClick={() => {
                            const recover = prompt(`Monto recuperado (potencial: ${fmt(r.total_potential_loss)}):`);
                            if (recover != null) updateStatus(r.id, 'claim_won', parseFloat(recover) || 0);
                          }}
                            className="px-2 py-0.5 bg-green-500/10 text-green-600 hover:bg-green-500/20 rounded text-xs">
                            Cubierto
                          </button>
                          <button onClick={() => updateStatus(r.id, 'claim_lost')}
                            className="px-2 py-0.5 bg-red-500/10 text-red-600 hover:bg-red-500/20 rounded text-xs">
                            Perdido
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!rows.length && !loading && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-[var(--color-text-secondary)]">
                  Sin casos con los filtros actuales
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {drillId !== null && <DrillModal id={drillId} onClose={() => setDrillId(null)} onUpdated={load} />}
    </div>
  );
}

function KPICard({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon?: React.ReactNode }) {
  return (
    <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-[var(--color-text-secondary)]">{label}</span>
        {icon}
      </div>
      <div className="text-xl font-bold text-[var(--color-text)]">{value}</div>
      {sub && <div className="text-xs text-[var(--color-text-secondary)]">{sub}</div>}
    </div>
  );
}

function TrendingIcon() {
  return <span className="text-blue-500 text-base">↻</span>;
}

function DrillModal({ id, onClose, onUpdated }: { id: number; onClose: () => void; onUpdated: () => void }) {
  const can = useCan();
  const canOperate = can('finance.operate');
  const [data, setData] = useState<any>(null);
  const [notes, setNotes] = useState('');
  const [mlClaimId, setMlClaimId] = useState('');

  useEffect(() => {
    api.get<any>(`/finance/pending-claims/${id}`).then(r => {
      if (!r.data) return;
      setData(r.data);
      setNotes(r.data.claim?.notes ?? '');
      setMlClaimId(r.data.claim?.ml_claim_id ? String(r.data.claim.ml_claim_id) : '');
    });
  }, [id]);

  const save = async () => {
    await api.put(`/finance/pending-claims/${id}`, {
      notes,
      ml_claim_id: mlClaimId ? Number(mlClaimId) : null,
    });
    onUpdated();
    onClose();
  };

  if (!data) return null;
  const { claim, order, ledger, mp_releases, ml_claim_link } = data;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[var(--color-bg-card)] border-b border-[var(--color-border)] p-4 flex items-center justify-between">
          <h3 className="font-bold">Caso #{id} — Order {claim.ml_order_id} ({claim.context_site})</h3>
          <button onClick={onClose} className="text-[var(--color-text-secondary)]">✕</button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-[var(--color-text-secondary)]">Pérdida potencial:</span> <strong>{fmt(claim.total_potential_loss)}</strong></div>
            <div><span className="text-[var(--color-text-secondary)]">Tipo:</span> {DETECTION_LABEL[claim.detection_type as DetectionType]}</div>
            <div><span className="text-[var(--color-text-secondary)]">Amazon perdido:</span> {fmt(claim.amazon_cost_lost)}</div>
            <div><span className="text-[var(--color-text-secondary)]">Mediation MP:</span> {fmt(claim.mediation_charged)}</div>
            <div><span className="text-[var(--color-text-secondary)]">Estado actual:</span> <span className={`px-2 py-0.5 text-xs border rounded ${STATUS_COLOR[claim.status as Status]}`}>{STATUS_LABEL[claim.status as Status]}</span></div>
            {ml_claim_link && (
              <div className="col-span-2">
                <a href={ml_claim_link} target="_blank" rel="noopener" className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline">
                  <ExternalLink className="w-3 h-3" /> Abrir order en panel ML
                </a>
              </div>
            )}
          </div>

          {order && (
            <div>
              <h4 className="text-sm font-semibold mb-1">Order</h4>
              <div className="text-xs grid grid-cols-2 gap-2">
                <div><span className="text-[var(--color-text-secondary)]">Status:</span> {String(order.status)}</div>
                <div><span className="text-[var(--color-text-secondary)]">Vendido:</span> {String(order.date_created).slice(0, 10)}</div>
                <div><span className="text-[var(--color-text-secondary)]">Cerrado:</span> {String(order.date_closed ?? '—').slice(0, 10)}</div>
                <div><span className="text-[var(--color-text-secondary)]">Pack:</span> {String(order.pack_id ?? '—')}</div>
              </div>
            </div>
          )}

          <div>
            <h4 className="text-sm font-semibold mb-1">Ledger ({ledger.length})</h4>
            <table className="w-full text-xs">
              <thead className="bg-[var(--color-bg-subtle)]">
                <tr><th className="text-left px-2 py-1">Event</th><th className="text-right px-2 py-1">Amount</th><th className="text-left px-2 py-1">Date</th></tr>
              </thead>
              <tbody>
                {ledger.map((e: any, i: number) => (
                  <tr key={i} className="border-t border-[var(--color-border)]">
                    <td className="px-2 py-1 font-mono">{e.event_type}</td>
                    <td className="px-2 py-1 text-right font-mono">{fmt(e.amount)}</td>
                    <td className="px-2 py-1 text-[var(--color-text-secondary)]">{e.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {mp_releases.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-1">MP CSV ({mp_releases.length})</h4>
              <table className="w-full text-xs">
                <thead className="bg-[var(--color-bg-subtle)]">
                  <tr><th className="text-left px-2 py-1">Description</th><th className="text-right px-2 py-1">Gross</th><th className="text-left px-2 py-1">Date</th><th className="text-left px-2 py-1">Wallet</th></tr>
                </thead>
                <tbody>
                  {mp_releases.map((r: any, i: number) => (
                    <tr key={i} className="border-t border-[var(--color-border)]">
                      <td className="px-2 py-1">{r.description}</td>
                      <td className="px-2 py-1 text-right font-mono">{fmt(r.gross_amount)}</td>
                      <td className="px-2 py-1 text-[var(--color-text-secondary)]">{String(r.date).slice(0, 10)}</td>
                      <td className="px-2 py-1 font-mono text-[var(--color-text-secondary)]">{r.mp_account_id ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div>
            <label className="block text-xs text-[var(--color-text-secondary)] mb-1">ml_claim_id (opcional, si lo encontraste en panel)</label>
            <input value={mlClaimId} onChange={e => setMlClaimId(e.target.value)} disabled={!canOperate}
              className="w-full bg-[var(--color-bg-subtle)] border border-[var(--color-border)] rounded px-2 py-1 text-sm disabled:opacity-60" />
          </div>

          <div>
            <label className="block text-xs text-[var(--color-text-secondary)] mb-1">Notas</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} disabled={!canOperate}
              className="w-full bg-[var(--color-bg-subtle)] border border-[var(--color-border)] rounded px-2 py-1 text-sm disabled:opacity-60" />
          </div>

          <div className="flex gap-2 sticky bottom-0 bg-[var(--color-bg-card)] py-2 border-t border-[var(--color-border)]">
            <Can cap="finance.operate">
              <button onClick={save} className="px-4 py-1.5 bg-[var(--color-primary)] text-white rounded text-sm">Guardar</button>
            </Can>
            <button onClick={onClose} className="px-4 py-1.5 border border-[var(--color-border)] rounded text-sm">Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}
